import React, { useState, useEffect } from 'react';
import {
  Camera,
  AlertTriangle,
  Usb,
  TrendingUp,
  Loader2,
  ShieldCheck,
} from 'lucide-react';
import {
  getDashboardStats,
  getActivityTimeline,
  getRiskSummary,
  getLatestVersion,
} from '../services/api';
import RiskMethodology from '../components/Dashboard/RiskMethodology';
import QuickActions from '../components/Dashboard/QuickActions';
import SystemHealth from '../components/Dashboard/SystemHealth';
import ThreatMap from '../components/Dashboard/ThreatMap';
import TopRisks from '../components/Dashboard/TopRisks';
import BehaviorScore from '../components/Dashboard/BehaviorScore';
import LiveEventFeed from '../components/Dashboard/LiveEventFeed';
import ComparisonChart from '../components/Dashboard/ComparisonChart';
import RecentScreenshots from '../components/Dashboard/RecentScreenshots';
import BrowserSummary from '../components/Dashboard/BrowserSummary';
import PrinterSummary from '../components/Dashboard/PrinterSummary';
import AutoRefreshControl from '../components/AutoRefreshControl';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { PageHeader } from '../components/ui/page-header';
import { StatTile } from '../components/ui/stat-tile';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Progress } from '../components/ui/progress';
import { cn } from '../lib/utils';

dayjs.extend(relativeTime);

function riskTone(score) {
  if (score >= 80) return 'destructive';
  if (score >= 50) return 'warning';
  if (score >= 20) return 'primary';
  return 'success';
}

function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [activities, setActivities] = useState([]);
  const [riskSummary, setRiskSummary] = useState(null);
  const [latestVersion, setLatestVersion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadDashboardData = async () => {
    try {
      if (!stats) setLoading(true);
      const [statsRes, activitiesRes, riskRes, versionRes] = await Promise.all([
        getDashboardStats(),
        getActivityTimeline(24),
        getRiskSummary(),
        getLatestVersion().catch(() => ({ success: false, version: 'N/A' })),
      ]);
      setStats(statsRes.data.data);
      setActivities(activitiesRes.data.data);
      setRiskSummary(riskRes.data.data);
      setLatestVersion(versionRes.version || 'N/A');
      setError(null);
    } catch (err) {
      console.error('Failed to load dashboard data:', err);
      setError('Failed to load dashboard data. Make sure the backend service is running.');
    } finally {
      setLoading(false);
    }
  };

  const autoRefresh = useAutoRefresh(loadDashboardData, 30000, true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  if (loading && !stats) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertTriangle />
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  const avgRisk = riskSummary?.average_risk_score ?? 0;
  const tone = riskTone(avgRisk);
  const toneClass = {
    destructive: 'bg-destructive',
    warning: 'bg-warning',
    primary: 'bg-primary',
    success: 'bg-success',
  }[tone];

  return (
    <div className="mx-auto flex max-w-[1400px] flex-col gap-6">
      <PageHeader
        title="Security Dashboard"
        description={
          <>
            Real-time monitoring and threat detection
            {latestVersion && (
              <span className="ml-3 font-medium text-primary">
                • Latest worker version: {latestVersion}
              </span>
            )}
          </>
        }
        actions={<AutoRefreshControl {...autoRefresh} />}
      />

      {/* Stat tiles */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile
          label="Screenshots Today"
          value={stats?.screenshots_today || 0}
          hint={`Total: ${stats?.total_screenshots || 0}`}
          icon={Camera}
          tone="primary"
        />
        <StatTile
          label="Incidents Today"
          value={stats?.incidents_today || 0}
          hint={`High risk: ${stats?.high_risk_incidents || 0}`}
          icon={AlertTriangle}
          tone="destructive"
        />
        <StatTile
          label="USB Events"
          value={stats?.usb_events_today || 0}
          hint="Device connections"
          icon={Usb}
          tone="warning"
        />
        <StatTile
          label="Session"
          value={stats?.active_session || 'N/A'}
          hint="Active monitoring"
          icon={TrendingUp}
          tone="success"
        />
      </div>

      {/* Risk + behavior + top risks */}
      <div className="grid gap-4 lg:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-3">
            <CardTitle className="text-base">Risk Summary</CardTitle>
            {!loading && (
              <Badge variant="success">
                <TrendingUp className="h-3 w-3" />
                Live
              </Badge>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            {riskSummary ? (
              <>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Total Incidents</span>
                  <span className="text-xl font-semibold text-primary">
                    {riskSummary.total_incidents}
                  </span>
                </div>

                <div className="flex flex-wrap gap-2">
                  <Badge variant={riskSummary.by_severity.low > 0 ? 'success' : 'outline'}>
                    Low: {riskSummary.by_severity.low}
                  </Badge>
                  <Badge variant={riskSummary.by_severity.medium > 0 ? 'default' : 'outline'}>
                    Medium: {riskSummary.by_severity.medium}
                  </Badge>
                  <Badge variant={riskSummary.by_severity.high > 0 ? 'warning' : 'outline'}>
                    High: {riskSummary.by_severity.high}
                  </Badge>
                  <Badge
                    variant={
                      riskSummary.by_severity.critical > 0 ? 'destructive' : 'outline'
                    }
                  >
                    Critical: {riskSummary.by_severity.critical}
                  </Badge>
                </div>

                <div className="rounded-lg bg-muted/40 p-4">
                  <div className="text-xs uppercase tracking-wider text-muted-foreground">
                    Average Risk Score
                  </div>
                  <div className="mt-1 flex items-baseline gap-2">
                    <span className="text-3xl font-bold text-primary tabular-nums">
                      {avgRisk.toFixed(1)}
                    </span>
                    <span className="text-sm text-muted-foreground">/ 100</span>
                  </div>
                  <Progress
                    value={Math.min(avgRisk, 100)}
                    className="mt-3"
                    indicatorClassName={cn('transition-all duration-500', toneClass)}
                  />
                </div>

                {riskSummary.total_incidents === 0 && (
                  <Alert variant="success">
                    <ShieldCheck />
                    <AlertDescription>
                      No incidents detected. System operating normally.
                    </AlertDescription>
                  </Alert>
                )}
                {riskSummary.by_severity.critical > 0 && (
                  <Alert variant="destructive">
                    <AlertTriangle />
                    <AlertDescription>
                      {riskSummary.by_severity.critical} critical incident
                      {riskSummary.by_severity.critical > 1 ? 's' : ''} require immediate
                      attention!
                    </AlertDescription>
                  </Alert>
                )}
              </>
            ) : (
              <div className="flex h-48 items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            )}
          </CardContent>
        </Card>

        <BehaviorScore stats={stats} activities={activities} />
        <TopRisks activities={activities} />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <ThreatMap activities={activities} />
        </div>
        <SystemHealth stats={stats} />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <ComparisonChart activities={activities} />
        </div>
        <QuickActions onRefresh={loadDashboardData} />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <RecentScreenshots />
        <LiveEventFeed activities={activities} autoRefresh={true} />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <RiskMethodology />
        <BrowserSummary />
        <PrinterSummary />
      </div>
    </div>
  );
}

export default DashboardPage;
